import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import 'dayjs/locale/fr';
import 'dayjs/locale/es';
import 'dayjs/locale/de';
import 'dayjs/locale/pl';
import 'dayjs/locale/pt';
import 'dayjs/locale/bg';
import 'dayjs/locale/cs';
import 'dayjs/locale/hu';
import 'dayjs/locale/ro';
import 'dayjs/locale/sk';
import 'dayjs/locale/sv';
import { configService, RootState } from '@umanforge/common';
import { ClientService } from 'services/client.service';
import { SiteService } from 'services/site.service';
import { SectorService } from 'services/sector.service';
import { BaseActionTicketComponent } from './base_action_ticket.component';
import { UserIcon, CalendarIcon, RecurringIcon, ThemeIcon } from '@umanforge/common';
import { LoggerService } from '@umanforge/common';

dayjs.extend( LocalizedFormat );
dayjs.extend( customParseFormat );

const logger = new LoggerService( 'FlashmeetingActionTicketComponent' );

interface FlashmeetingActionTicketProps {
  id: string;
  eventId?: string;
  engagementId?: string;
  eventManagerId?: string;
  clientId?: string;
  siteId?: string;
  sectorId?: string;
  eventManagerName?: string;
  title: string;
  author: string;
  createdDate: string;
  engagementDate: string;
  theme: {
    name: string;
    color: string;
  };
  daysLate: number;
  isRecurrent: boolean;
  isShared: boolean;
  isCommon: boolean;
  hierarchy?: {
    client?: string;
    site?: string;
    sector?: string;
    eventManager?: string;
  };
}

export function FlashmeetingActionTicketComponent( {
  id,
  eventId,
  eventManagerId,
  eventManagerName,
  clientId,
  siteId,
  sectorId,
  title,
  author,
  createdDate,
  engagementDate,
  theme,
  daysLate,
  isRecurrent,
  isShared,
  isCommon,
  hierarchy,
}: FlashmeetingActionTicketProps ) {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );

  const [ clientName, setClientName ] = useState<string | null>( null );
  const [ siteName, setSiteName ] = useState<string | null>( null );
  const [ sectorName, setSectorName ] = useState<string | null>( null );

  const handleClick = async () => {
    const targetEngagementId = id;

    if ( eventManagerId && clientId && siteId && sectorId && eventId && targetEngagementId ) {
      try {
        const flashmeetingUrl = await configService.getFlashmeetingUrl();

        const baseUrl = flashmeetingUrl.endsWith( '/' ) ? flashmeetingUrl.slice( 0, -1 ) : flashmeetingUrl;
        const navigateUrl = `${baseUrl}/${clientId}/${siteId}/${sectorId}/${eventManagerId}/${eventId}/${targetEngagementId}`;
        window.location.href = navigateUrl;
      } catch ( error ) {
        logger.error( 'Error during navigation:', error );
      }
    } else {
      logger.warn( 'Missing parameters for navigation:', { eventManagerId, clientId, siteId, sectorId, eventId, targetEngagementId } );
    }
  };

  // Configurer la locale de dayjs et reformatter les dates
  useEffect( () => {
    dayjs.locale( i18n.language );
  }, [ i18n.language ] );

  // Résoudre les IDs en noms si hierarchy absent
  useEffect( () => {
    let mounted = true;

    ( async () => {
      try {
        if ( !hierarchy?.client && clientId ) {
          const client = await ClientService.getClient( clientId, token, dispatch );
          if ( mounted ) {setClientName( client?.name ?? null );}
        }
        if ( !hierarchy?.site && siteId ) {
          const site = await SiteService.getSite( siteId, token, dispatch );
          if ( mounted ) {setSiteName( site?.name ?? null );}
        }
        if ( !hierarchy?.sector && sectorId ) {
          const sector = await SectorService.getSector( sectorId, token, dispatch );
          if ( mounted ) {setSectorName( sector?.name ?? null );}
        }
      } catch ( error ) {
        logger.error( 'Error resolving hierarchy names:', error );
      }
    } )();

    return () => { mounted = false; };
  }, [ hierarchy, clientId, siteId, sectorId, token, dispatch ] );

  // Reformatter les dates en fonction de la locale
  const formattedCreatedDate = useMemo( () => {
    dayjs.locale( i18n.language );
    return dayjs( createdDate, 'DD/MM/YYYY' ).format( 'L' );
  }, [ createdDate, i18n.language ] );

  const formattedEngagementDate = useMemo( () => {
    dayjs.locale( i18n.language );
    return dayjs( engagementDate, 'DD/MM/YYYY' ).format( 'L' );
  }, [ engagementDate, i18n.language ] );

  const header = (
    <div className='flex items-start justify-between gap-4 mb-3'>
      <h4 className='text-gray-800 font-semibold flex-1 min-w-0 truncate'>
        { title }
      </h4>
      <div className='flex items-center gap-2 px-3 py-1 rounded-full text-white text-xs font-semibold whitespace-nowrap' style={ { backgroundColor: theme.color } }>
        <ThemeIcon className='w-4 h-4' />
        <span>{ theme.name }</span>
      </div>
    </div>
  );

  const tags = (
    <>
      {
        daysLate > 0 &&
        <span className='text-xs font-semibold px-2 py-1 rounded-full bg-red-100 text-red-800'>
          { t( 'days_late' ) }: { daysLate }
        </span>
      }
      {
        isRecurrent &&
        <span className='text-xs font-semibold px-2 py-1 rounded-full bg-blue-100 text-blue-800 flex items-center gap-1'>
          <RecurringIcon className='w-3 h-3' />
          <span>{ t( 'recurrent' ) }</span>
        </span>
      }
      {
        isShared &&
        <span className='text-xs font-semibold px-2 py-1 rounded-full bg-green-100 text-green-800'>
          { t( 'shared' ) }
        </span>
      }
      {
        isCommon &&
        <span className='text-xs font-semibold px-2 py-1 rounded-full bg-purple-100 text-purple-800'>
          { t( 'common' ) }
        </span>
      }
    </>
  );

  // Footer - hiérarchie puis auteur + date en dessous
  const footer = {
    left: (
      <div className='flex flex-col gap-1 text-xs text-gray-600'>
        <div className='flex gap-4'>
          {
            ( () => {
              const parts: string[] = [];
              if ( hierarchy?.client || clientName ) { parts.push( hierarchy?.client ?? clientName! ); }
              if ( hierarchy?.site || siteName ) { parts.push( hierarchy?.site ?? siteName! ); }
              if ( hierarchy?.sector || sectorName ) { parts.push( hierarchy?.sector ?? sectorName! ); }
              if ( hierarchy?.eventManager || eventManagerName ) { parts.push( hierarchy?.eventManager ?? eventManagerName! ); }
              return parts.length ? (
                <span className='text-xs text-gray-600 break-words'>
                  { parts.map( ( part, idx ) => {return (
                    <span key={ idx } className='inline'>
                      { part }
                      { idx < parts.length - 1 && '/' }
                      { idx === 1 && parts.length > 2 ? <wbr /> : null }
                    </span>
                  );} ) }
                </span>
              ) : null;
            } )()
          }
        </div>
        <div className='flex items-center gap-1'>
          <UserIcon className='w-4 h-4 text-gray-500' />
          <span className='font-medium'>{ author }</span>
          <CalendarIcon className='w-4 h-4 text-gray-500' />
          <span>{ formattedCreatedDate }</span>
        </div>
      </div>
    ),
    right: undefined,
  };

  return (
    <BaseActionTicketComponent
      borderColor='border-l-4 border-indigo-500'
      backgroundColor='bg-orange-100'
      header={ header }
      title=''
      subtitle={
        <div className='flex justify-between gap-4'>
          <div className='space-y-1'>
            <p className='text-sm text-gray-600'>
              { t( 'common:engagement' ) } : { formattedEngagementDate }
            </p>
          </div>
          <div className='flex flex-col gap-1'>
            { tags }
          </div>
        </div>
      }
      hierarchy={ hierarchy }
      footer={ footer }
      onClick={ handleClick }
    />
  );
}
